import Vue from 'vue';
import store from 'store';
import on from 'dom-event';
import debounce from 'lodash.debounce';
import {
  isMobile,
  decode,
  convertGQLToProductJson,
  htmlDecode,
} from 'utils/general';
import inViewport from 'in-viewport';
import {
  listProductsEventData,
  buildItemTrackingData,
  trackProductClick,
  setLocalStorageProperties,
} from 'lib/tracking';
import datalayer from 'lib/datalayer';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import {A11y, Scrollbar, Keyboard} from "swiper/modules";

export default (el) =>
  new Vue({
    el,
    store,
    components: {
      'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
      'yotpo-stars': () => import('../yotpo-stars/yotpo-stars.vue'),
    },
    data: () => ({
      flickity: null,
      swiperOptions: {
        slidesPerView: 1.3,
        spaceBetween: 14,
        centeredSlides: true,
        loop: true,
        autoHeight: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
        },
        breakpoints: {
          769: {
            slidesPerView: 2,
            spaceBetween: 16,
            centeredSlides: false,
            loop: false,
          },
          1440: {
            slidesPerView: 2.32,
            spaceBetween: 20,
            centeredSlides: false,
            loop: false,
          }
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous review',
          nextSlideMessage: 'Next review',
          firstSlideMessage: 'This is the first review',
          lastSlideMessage: 'This is the last review',
          paginationBulletMessage: 'Go to review {{index}}',
          slideLabelMessage: 'Review {{index}} of {{slidesLength}}',
          containerMessage: 'Product Reviews Carousel'
        },
        modules: [Scrollbar, A11y, Keyboard]
      },
      isMobile: isMobile(),
      slidesToShow: 2.32,
      parentTitle: '',
      listType: 'behind-experience',
    }),
    beforeMount() {
      this.parentTitle = this.$el.dataset.title;
    },
    mounted() {
      inViewport(this.$el, () => {
        this.trackView();
      });
    },
    computed: {
      productsDecoded() {
        const productsData = JSON.parse(decode(this.$el.dataset.products));
        return productsData.map((p, index) => ({ ...p, index: index }));
      },
    },
    methods: {
      onResize() {},
      async trackView() {
        const extraData = {
          listName: this.parentTitle || undefined,
          listId: this.listType || undefined,
          origin: document.body.dataset.origin,
          originProduct: document.body.dataset.originProduct,
        };

        const { products, hasBundleBuilder } = await listProductsEventData(
          this.productsDecoded,
          extraData,
        );

        let eventData = {
          ecommerce: {
            item_list_id: this.listType || undefined,
            item_list_name: this.parentTitle || undefined,
            section: 'behind experience section',
            items: products,
          },
        };
        if (hasBundleBuilder) {
          eventData = {
            ...eventData,
            site_section: window.__GLOBAL__.siteSection,
            component_name: this.listType || undefined,
            bundle_type: 'bundle builder',
          }
        }
        // track visible elements on page load
        datalayer.dataLayerPush('view_item_list', eventData);
      },
      async onItemClicked(index, url) {
        const product = this.productsDecoded[index - 1];
        const otherData = {
          origin: document.body.dataset.origin,
          originProduct: document.body.dataset.originProduct,
          index: index,
          listName: this.parentTitle,
          listId: this.listType,
        };

        const listData = {
          listName: this.parentTitle,
          listId: this.listType,
          index: index,
          section: 'behind experience',
          moduleName: 'reviews slider'
        };

        setLocalStorageProperties(otherData, listData);

        let itemObject = [];

        if (product.isBundle) {
          otherData.bundleName = product.title;
          otherData.bundleId = product.id;
          listData.bundleName = product.title;
          listData.bundleId = product.id;
          const bundleProducts = product.is_json
            ? product.bundled_products
            : product.metafields.accentuate.bundled_products;

          const bundleProductHandles = bundleProducts
            ? bundleProducts.map((handle) => handle.split('|')[0])
            : [];
          itemObject = await Promise.all(
            bundleProductHandles.map(async (handle) => {
              const bundleResponse = await storefront.request(
                gqlQueries.product,
                {
                  handle,
                },
              );
              const formatted = convertGQLToProductJson(bundleResponse.product);
              formatted.isBundle = true;
              const itemObject = buildItemTrackingData(formatted, 1, otherData);
              return itemObject;
            }),
          );
        } else {
          itemObject = buildItemTrackingData(product, 1, otherData);
        }

        trackProductClick('select_item', itemObject, listData);
        window.location.href = url;
      },
    },
  });
